<template>
    <div>
        <input
            v-if="type === 'create'"
            type="hidden"
            name="data_descriptor"
            :value="inputs.dataDescriptor"
        />
        <input
            v-if="type === 'create'"
            type="hidden"
            name="data_value"
            :value="inputs.dataValue"
        />

        <div class="grid grid-cols-6 gap-6">
            <div
                v-if="type === 'create'"
                :class="[
                    includeDefaultField ? 'sm:col-span-3' : 'sm:col-span-4',
                    'col-span-6',
                ]"
            >
                <label
                    for="card_number"
                    class="block text-sm font-medium text-gray-700"
                >
                    Card Number
                </label>
                <div class="mt-1 relative rounded-md shadow-sm">
                    <cleave
                        v-model="inputs.card_number"
                        :options="{ creditCard: true, delimiter: '  ' }"
                        :ref="refs.card_number"
                        id="card_number"
                        :name="cardNumberInputName"
                        type="text"
                        inputmode="numeric"
                        autocomplete="cc-number"
                        required
                        :class="[
                            hasError(errors, 'card_number') ||
                            cardTokenizationErrors.card_number
                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                            'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                        ]"
                        :aria-invalid="
                            hasError(errors, 'card_number') ||
                            cardTokenizationErrors.card_number
                                ? 'true'
                                : 'false'
                        "
                        :aria-describedby="
                            hasError(errors, 'card_number') ||
                            cardTokenizationErrors.card_number
                                ? 'card_number-error'
                                : ''
                        "
                    />
                    <div
                        v-if="cardType"
                        class="absolute inset-y-0 right-0 pr-1 flex items-center pointer-events-none"
                    >
                        <svg-vue
                            :icon="svgPathForCardType(cardType)"
                            :key="cardType"
                            class="w-10 h-auto rounded shadow"
                            aria-hidden="true"
                        />
                    </div>
                </div>
                <p
                    v-if="
                        hasError(errors, 'card_number') ||
                        cardTokenizationErrors.card_number
                    "
                    class="mt-2 text-sm text-red-600"
                    id="card_number-error"
                >
                    {{
                        hasError(errors, "card_number")
                            ? error(errors, "card_number")
                            : cardTokenizationErrors.card_number
                    }}
                </p>
            </div>

            <div v-else class="col-span-6 sm:col-span-2">
                <div class="mt-1 sm:mt-3 flex items-center">
                    <svg-vue
                        :icon="svgPathForCardType(paymentMethodCardType)"
                        :key="paymentMethodCardType"
                        class="w-16 h-auto rounded shadow"
                        aria-hidden="true"
                    />
                    <span class="ml-4 text-sm font-medium text-gray-700">
                        Ending in {{ paymentMethodCardNumber }}
                    </span>
                </div>
            </div>

            <div
                v-if="includeDefaultField"
                :class="[
                    type === 'create'
                        ? 'sm:order-none sm:col-start-5'
                        : 'order-last',
                    'col-span-6 sm:col-span-2',
                ]"
            >
                <label
                    class="block text-sm sm:text-center font-medium text-gray-700"
                >
                    Set as Default Card
                </label>
                <input
                    type="hidden"
                    name="default"
                    :value="inputs.default ? '1' : ''"
                />
                <SwitchGroup
                    as="li"
                    class="pt-3 pb-1 flex items-center justify-between sm:justify-center text-transparent"
                >
                    <Switch
                        data-test="default"
                        name="default"
                        v-model="inputs.default"
                        :class="[
                            inputs.default
                                ? 'bg-nutreance-green'
                                : 'bg-gray-200',
                            'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none',
                        ]"
                    >
                        <span class="sr-only">Toggle Default</span>
                        <span
                            aria-hidden="true"
                            :class="[
                                inputs.default
                                    ? 'translate-x-5'
                                    : 'translate-x-0',
                                'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                            ]"
                        />
                    </Switch>
                </SwitchGroup>
            </div>

            <div class="col-span-6 sm:col-span-2">
                <label
                    for="expiration"
                    class="block text-sm font-medium text-gray-700"
                >
                    Expiration (MM/YY)
                </label>
                <div class="mt-1 relative rounded-md shadow-sm">
                    <cleave
                        v-model="inputs.expiration"
                        :options="{ date: true, datePattern: ['m', 'y'] }"
                        :ref="refs.expiration"
                        id="expiration"
                        name="expiration"
                        type="text"
                        inputmode="numeric"
                        autocomplete="cc-exp"
                        required
                        :class="[
                            hasError(errors, 'expiration') ||
                            cardTokenizationErrors.expiration
                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                            'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                        ]"
                        :aria-invalid="
                            hasError(errors, 'expiration') ||
                            cardTokenizationErrors.expiration
                                ? 'true'
                                : 'false'
                        "
                        :aria-describedby="
                            hasError(errors, 'expiration') ||
                            cardTokenizationErrors.expiration
                                ? 'expiration-error'
                                : ''
                        "
                    />
                    <div
                        v-if="
                            hasError(errors, 'expiration') ||
                            cardTokenizationErrors.expiration
                        "
                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                    >
                        <ExclamationCircleIcon
                            class="h-5 w-5 text-red-500"
                            aria-hidden="true"
                        />
                    </div>
                </div>
                <p
                    v-if="
                        hasError(errors, 'expiration') ||
                        cardTokenizationErrors.expiration
                    "
                    class="mt-2 text-sm text-red-600"
                    id="expiration-error"
                >
                    {{
                        hasError(errors, "expiration")
                            ? error(errors, "expiration")
                            : cardTokenizationErrors.expiration
                    }}
                </p>
            </div>
        </div>

        <div class="pt-6" v-if="addresses.length > 0">
            <label class="block text-sm font-medium text-gray-700">
                Billing Address
            </label>
            <input type="hidden" name="address_id" :value="inputs.address_id" />

            <RadioGroup v-model="inputs.address_id">
                <RadioGroupLabel class="sr-only">
                    Billing Address
                </RadioGroupLabel>
                <div class="bg-white rounded-md -space-y-px mt-1">
                    <RadioGroupOption
                        :data-test="'address-' + address.id"
                        as="template"
                        v-for="(address, addressId) in addresses"
                        :key="address.id"
                        :value="address.id"
                        v-slot="{ checked }"
                    >
                        <div
                            :class="[
                                addressId === 0
                                    ? 'rounded-tl-md rounded-tr-md'
                                    : '',
                                checked
                                    ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                                    : 'border-gray-200',
                                'relative border p-4 flex cursor-pointer focus:outline-none',
                            ]"
                        >
                            <div class="flex items-center">
                                <span
                                    :class="[
                                        checked
                                            ? 'bg-nutreance-green border-transparent'
                                            : 'bg-white border-gray-300',
                                        'h-4 w-4 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                                    ]"
                                    aria-hidden="true"
                                >
                                    <span
                                        class="rounded-full bg-white w-1.5 h-1.5"
                                    />
                                </span>
                            </div>

                            <div class="ml-3 flex flex-col">
                                <RadioGroupLabel
                                    as="span"
                                    :class="[
                                        checked
                                            ? 'text-emerald-900'
                                            : 'text-gray-900',
                                        'block text-sm font-medium',
                                    ]"
                                >
                                    {{ address.name }}
                                </RadioGroupLabel>
                                <RadioGroupDescription
                                    as="span"
                                    :class="[
                                        checked
                                            ? 'text-emerald-700'
                                            : 'text-gray-500',
                                        'block text-sm',
                                    ]"
                                >
                                    {{
                                        address.line1 +
                                        (address.line2
                                            ? " " + address.line2
                                            : "")
                                    }}, {{ address.city }}, {{ address.state }}
                                    {{ address.postal_code }},
                                    {{ address.country_code }}
                                </RadioGroupDescription>
                            </div>
                        </div>
                    </RadioGroupOption>

                    <RadioGroupOption
                        id="use-new-address"
                        as="template"
                        value=""
                        v-slot="{ checked }"
                    >
                        <div
                            :class="[
                                checked
                                    ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                                    : 'border-gray-200',
                                'rounded-bl-md rounded-br-md relative border py-6 px-4 flex cursor-pointer focus:outline-none',
                            ]"
                        >
                            <span
                                :class="[
                                    checked
                                        ? 'bg-nutreance-green border-transparent'
                                        : 'bg-white border-gray-300',
                                    'h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                                ]"
                                aria-hidden="true"
                            >
                                <span
                                    class="rounded-full bg-white w-1.5 h-1.5"
                                />
                            </span>

                            <div class="ml-3 flex flex-col">
                                <RadioGroupLabel
                                    as="span"
                                    :class="[
                                        checked
                                            ? 'text-emerald-900'
                                            : 'text-gray-900',
                                        'block text-sm font-medium',
                                    ]"
                                >
                                    Use a New Address
                                </RadioGroupLabel>
                            </div>
                        </div>
                    </RadioGroupOption>
                </div>
            </RadioGroup>
        </div>

        <AddressFormFields
            class="pt-4 sm:pt-6"
            v-if="!inputs.address_id"
            :address="addresses.length > 0 ? addresses[0] : []"
            :countries="countries"
            :emailOptIn="emailOptIn"
            :errors="errors"
            :includeDefaultField="false"
            :includeVerifyField="includeVerifyField"
            :initialCountry="initialCountry"
            :initialStates="initialStates"
            :old="old"
            :type="addresses.length > 0 ? 'edit' : 'create'"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, reactive, ref } from "vue";
import { ExclamationCircleIcon } from "@heroicons/vue/solid";
import {
    RadioGroup,
    RadioGroupDescription,
    RadioGroupLabel,
    RadioGroupOption,
    Switch,
    SwitchGroup,
} from "@headlessui/vue";
import type { AppEmitter } from "../../emitter";
import { useForm } from "../../functions/useForm";
import AddressFormFields from "../addresses/AddressFormFields.vue";
import Cleave from "vue-cleave-component";
import { DateTime } from "luxon";
import { usePaymentMethod } from "../../functions/usePaymentMethod";
import { useAcceptJs } from "../../functions/useAcceptJs";

const props = withDefaults(
    defineProps<{
        address?: any;
        addresses?: any;
        countries?: any;
        csrf?: string;
        default?: string;
        defaultAddressId?: string;
        emailOptIn?: string;
        errors?: any;
        includeDefaultField?: boolean;
        includeVerifyField?: boolean;
        initialCountry?: any;
        initialStates?: any;
        old?: any;
        paymentGateway?: any;
        paymentMethod?: any;
        type?: string;
    }>(),
    {
        emailOptIn: "1",
        includeDefaultField: true,
        includeVerifyField: false,
    },
);

const emit = defineEmits<{
    (e: "cardDataTokenized"): void;
    (e: "cardDataTokenizationFailed"): void;
}>();

const emitter = inject<AppEmitter>("mitt")!;
const { error, hasError, oldInput } = useForm();
const { cardTypeSlug } = usePaymentMethod();
const {
    isCardValidationError,
    loadAcceptJs,
    tokenizeCardData,
    tokenizationError,
} = useAcceptJs();

const paymentMethodCardNumber = computed(() =>
    props.paymentMethod.card_number.replace(/X/g, ""),
);
const paymentMethodCardType = computed(() =>
    props.paymentMethod.card_type.toLowerCase().replace(/_/g, "-"),
);
const cardType = computed(() => cardTypeSlug(inputs.card_number));
const cardNumberInputName = ref<string | null>(
    props.type === "edit" ? "card_number" : null,
);

const refs: Record<string, any> = {
    card_number: ref<any>(null),
    expiration: ref<any>(null),
};

const inputs =
    props.type === "edit"
        ? reactive({
              card_number: props.paymentMethod.card_number,
              expiration: DateTime.fromISO(
                  props.paymentMethod.expiration,
              ).toFormat("LLyy"),
              dataDescriptor: "",
              dataValue: "",
              default: !!(Object.prototype.hasOwnProperty.call(
                  props.old,
                  "default",
              )
                  ? oldInput(props.old, "default")
                  : props.default),
              address_id: Object.prototype.hasOwnProperty.call(
                  props.old,
                  "address_id",
              )
                  ? oldInput(props.old, "address_id")
                      ? parseInt(oldInput(props.old, "address_id"))
                      : ""
                  : props.defaultAddressId
                    ? parseInt(props.defaultAddressId)
                    : "",
          })
        : reactive({
              card_number: "",
              expiration: "",
              dataDescriptor: "",
              dataValue: "",
              default: !!oldInput(props.old, "default"),
              address_id: Object.prototype.hasOwnProperty.call(
                  props.old,
                  "address_id",
              )
                  ? oldInput(props.old, "address_id")
                      ? parseInt(oldInput(props.old, "address_id"))
                      : ""
                  : props.defaultAddressId
                    ? parseInt(props.defaultAddressId)
                    : "",
          });

const cardTokenizationErrors = reactive<Record<string, string>>({
    card_number: "",
    expiration: "",
});

onMounted(() => {
    switch (true) {
        case !!hasError(props.errors, "card_number"):
            refs.card_number.value.$el.focus();
            break;
        case !!hasError(props.errors, "expiration"):
            refs.expiration.value.$el.focus();
            break;
        case !!hasError(props.errors, "name"):
        case !!hasError(props.errors, "line1"):
        case !!hasError(props.errors, "line2"):
        case !!hasError(props.errors, "city"):
        case !!hasError(props.errors, "state"):
        case !!hasError(props.errors, "postal_code"):
        case !!hasError(props.errors, "country_code"):
        case !!hasError(props.errors, "email"):
        case !!hasError(props.errors, "phone"):
            break;
        default:
            if (props.type === "create") {
                refs.card_number.value.$el.focus();
            } else {
                refs.expiration.value.$el.focus();
            }
    }

    if (props.type === "edit") {
        return;
    }

    loadAcceptJs(!!props.paymentGateway.sandbox);

    emitter.on("tokenizeCardData", async () => {
        cardTokenizationErrors.card_number = "";
        cardTokenizationErrors.expiration = "";

        try {
            const result = await tokenizeCardData(
                props.paymentGateway,
                inputs.card_number,
                inputs.expiration,
            );

            if (result) {
                inputs.dataDescriptor = result.dataDescriptor;
                inputs.dataValue = result.dataValue;
            } else {
                cardNumberInputName.value = "card_number";
            }

            nextTick(() => emit("cardDataTokenized"));
        } catch (error: any) {
            if (!isCardValidationError(error)) {
                cardNumberInputName.value = "card_number";
                inputs.dataDescriptor = "";
                inputs.dataValue = "";
                nextTick(() => emit("cardDataTokenized"));
                return;
            }

            const { field, message } = tokenizationError(error);

            cardTokenizationErrors[field] = message;
            nextTick(() => refs[field].value?.$el?.focus());
            emit("cardDataTokenizationFailed");
        }
    });
});

function svgPathForCardType(cardType: string) {
    return `payments/${cardType}`;
}
</script>
